import React, { useEffect, useState } from 'react';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

// Right-hand side panel for add / edit forms. Same props as CustomModal
// (open, setOpen, newForm, body, handleSaveData, title, showAction), so a
// dialog can be swapped for a panel without touching its form or save logic.
// `footer` replaces the Cancel / Save row (e.g. a wizard's Back / Next);
// `flush` drops the body padding for forms that lay out their own;
// `hideHeader` drops the title row (the footer has Cancel), keeping the
// title for screen readers.

const useStyles = makeStyles({
  paper: {
    width: 440,
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '16px 0 0 16px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '20px 20px 16px 24px',
    borderBottom: '1px solid #EEF1F6',
  },
  headerText: { flex: 1 },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: '#1F2733',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#5B6472',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 24px 24px',
    '&$flush': { padding: 0 },
    // The forms were sized for a centred dialog (300–400px boxes): let
    // them and their fields use the panel's full width.
    '& > *': { width: '100% !important', maxWidth: '100%' },
    '& .MuiFormControl-root': {
      width: '100%',
      minWidth: 0,
      marginLeft: 0,
      marginRight: 0,
    },
  },
  flush: {},
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '16px 24px',
    borderTop: '1px solid #EEF1F6',
    background: '#FAFBFD',
  },
});

export default function SidePanel({
  open, setOpen, newForm, body, handleSaveData, title, subtitle,
  showAction = true, actionLabel, width, footer, flush, hideHeader,
}) {
  const classes = useStyles();
  const close = () => setOpen(false);
  // Open one frame after mount, so panels that are mounted already open
  // (rendered only while needed) still slide in.
  const [shown, setShown] = useState(false);
  useEffect(() => { setShown(Boolean(open)); }, [open]);

  return (
    <Drawer
      anchor='right'
      open={shown}
      onClose={close}
      classes={{ paper: classes.paper }}
      PaperProps={{
        ...(typeof width === 'number' ? { style: { width } } : {}),
        ...(hideHeader ? { 'aria-label': title } : {}),
      }}
    >
      {hideHeader ? null : (
        <div className={classes.header}>
          <div className={classes.headerText}>
            <h2 className={classes.title}>{title}</h2>
            {subtitle ? <p className={classes.subtitle}>{subtitle}</p> : null}
          </div>
          <IconButton aria-label='Close' size='small' onClick={close}>
            <CloseRoundedIcon />
          </IconButton>
        </div>
      )}
      <div className={`${classes.body} ${flush ? classes.flush : ''}`}>{open ? body : null}</div>
      {footer ? <div className={classes.footer}>{footer}</div> : null}
      {!footer && showAction ? (
        <div className={classes.footer}>
          <Button color='primary' onClick={close}>Cancel</Button>
          <Button variant='contained' color='primary' onClick={handleSaveData}>
            {actionLabel || (newForm ? 'Save' : 'Update')}
          </Button>
        </div>
      ) : null}
    </Drawer>
  );
}
